/**
 * Help content — one entry per module/page (CLARID-UX-2026-06, PR 1).
 *
 * Typed as `Record<ModuleKey, ModuleHelpContent>` so the type-checker enforces:
 *   - every module in {@link ModuleKey} has exactly one entry;
 *   - every `feeds` / `needs` value is a real module key;
 *   - every `glossary` value is a real glossary term.
 *
 * Entries follow the CRA-Lending-Test template (graded A− in the UX audit):
 * a question title, one plain-language paragraph, 3 bullets (inputs / outputs /
 * where the data goes), and a single first action. `feeds` / `needs` encode the
 * cross-module graph that renders the connectivity moat (PR 2) and "Next step"
 * CTAs. No JSX — rendering is the component's job.
 */

import type { ModuleKey } from './keys';
import type { ModuleHelpContent } from './types';

export const helpContent: Record<ModuleKey, ModuleHelpContent> = {
  // ──────────────────────────────── Marketing ────────────────────────────────
  'marketing-upload': {
    title: 'What does Marketing review check?',
    summary:
      'Upload an ad, disclosure, or landing page and Clarid checks it against advertising and consumer-protection rules — including UDAAP, triggering terms, and required disclosures — before it goes live.',
    points: [
      'Inputs: the creative or page URL, plus the product type so the right rules run.',
      'Outputs: a pass/flag result with the specific rule and language at issue.',
      'Findings feed the Compliance Hub and the fair-lending content review.',
    ],
    firstAction: 'Upload a piece of content (or paste a landing-page URL) and pick its product type.',
    feeds: ['marketing-reports', 'fair-lending-content-findings', 'compliance-hub'],
    needs: ['product-profiles', 'custom-rules'],
    glossary: ['udaap'],
  },
  'marketing-reports': {
    title: 'What are Marketing reports?',
    summary:
      'A record of every reviewed piece of content and its result, exportable as an examiner-ready spreadsheet for your advertising compliance file.',
    points: [
      'Inputs: completed marketing reviews.',
      'Outputs: a filterable history and the "Examiner Spreadsheet" export.',
      'Feeds the board report’s marketing section.',
    ],
    firstAction: 'Filter to compliant-but-unsubmitted items, or export the examiner spreadsheet.',
    feeds: ['board-reports', 'compliance-hub'],
    needs: ['marketing-upload'],
    glossary: [],
  },
  'content-library': {
    title: 'What is the Content Library?',
    summary:
      'A central store of your marketing content and its review status, so approved and pending material is tracked in one place across its lifecycle.',
    points: [
      'Inputs: uploaded or imported marketing content.',
      'Outputs: each item’s status (draft → in review → approved/needs changes).',
      'Approved content is available for screening and reporting.',
    ],
    firstAction: 'Add a content item, or open one to see its review status.',
    feeds: ['marketing-upload', 'fair-lending-content-findings'],
    needs: [],
    glossary: [],
  },
  'pending-reviews': {
    title: 'What are Pending Reviews?',
    summary:
      'The marketing items waiting on a reviewer, with the current stage and owner so nothing stalls before publication.',
    points: [
      'Inputs: content submitted for review.',
      'Outputs: a queue showing stage, owner, and how long each item has waited.',
      'Approved items move to the Content Library and reports.',
    ],
    firstAction: 'Open the oldest item and either approve it or return it with notes.',
    feeds: ['content-library', 'marketing-reports'],
    needs: ['marketing-upload'],
    glossary: [],
  },
  'website-scan': {
    title: 'What is a Website Scan?',
    summary:
      'Clarid crawls your public website and checks the pages it finds against the same advertising and UDAAP rules used for uploaded content.',
    points: [
      'Inputs: a starting URL and a crawl depth (how many links deep to follow).',
      'Outputs: per-page findings with the rule and language flagged.',
      'Findings roll up into marketing reports and the Compliance Hub.',
    ],
    firstAction: 'Enter your site URL, set the crawl depth, and start a scan.',
    feeds: ['marketing-reports', 'fair-lending-content-findings'],
    needs: ['custom-rules'],
    glossary: ['udaap'],
    runtime: '1–5 min',
  },
  'social-media': {
    title: 'What does Social Media review check?',
    summary:
      'Screens social posts against advertising rules tied to the product being promoted, including UDAAP and required disclosures.',
    points: [
      'Inputs: the post text/creative and the product type it advertises.',
      'Outputs: a UDAAP score and any flagged language or missing disclosures.',
      'Results feed marketing reporting and content findings.',
    ],
    firstAction: 'Paste a post and select the product type it promotes.',
    feeds: ['marketing-reports', 'fair-lending-content-findings'],
    needs: ['product-profiles'],
    glossary: ['udaap'],
  },
  'mcp-checks': {
    title: 'What are MCP Checks?',
    summary:
      'Compliance checks run through Clarid’s Model Context Protocol connector, so an AI assistant (e.g. in ChatGPT or Claude) can screen content against your rules and return a reviewable result here.',
    points: [
      'Inputs: content submitted via the MCP connector.',
      'Outputs: the same pass/flag results as in-app review, with a review status.',
      'Results join your marketing review history.',
    ],
    firstAction: 'Review the latest MCP-submitted check and confirm or override its result.',
    feeds: ['marketing-reports'],
    needs: [],
    glossary: ['udaap'],
  },
  'marketing-analytics': {
    title: 'What do Marketing Analytics show?',
    summary:
      'Trends across your marketing reviews — volume, pass rate, and the most common violations — so you can see where content keeps tripping the rules.',
    points: [
      'Inputs: your completed marketing reviews over time.',
      'Outputs: pass-rate and violation-trend charts.',
      'Use it to prioritize rule fixes and training.',
    ],
    firstAction: 'Open the top-violations chart and click through to the underlying items.',
    feeds: ['training'],
    needs: ['marketing-upload'],
    glossary: [],
  },

  // ────────────────────────────────── HMDA ───────────────────────────────────
  'hmda-lar-builder': {
    title: 'What is the LAR Builder?',
    summary:
      'Assembles your Loan/Application Register (LAR) — the HMDA file of every covered application — from your loan data, so it’s ready to validate and file.',
    points: [
      'Inputs: your loan records and the reporting year.',
      'Outputs: a structured LAR with all required HMDA fields.',
      'The finished LAR flows into validation, then filing.',
    ],
    firstAction: 'Start a LAR for the reporting year and import your loan data.',
    feeds: ['hmda-lar-validation'],
    needs: [],
    glossary: ['gmi', 'lei'],
  },
  'hmda-field-guide': {
    title: 'What is the HMDA Field Guide?',
    summary:
      'A guided reference for HMDA data fields and reportability — answer a few screener questions and see which fields apply and how to code them.',
    points: [
      'Inputs: your answers to the reportability screener.',
      'Outputs: the field names, descriptions, and valid codes that apply.',
      'Use it while building or correcting a LAR.',
    ],
    firstAction: 'Answer the screener questions for your loan type.',
    feeds: ['hmda-lar-builder'],
    needs: [],
    glossary: ['gmi'],
  },
  'hmda-lar-validation': {
    title: 'What is LAR Validation?',
    summary:
      'Runs your LAR through the FFIEC edit checks — syntactical, validity, and quality — so errors are caught and fixed before you file with the CFPB.',
    points: [
      'Inputs: your LAR file (uploaded or built in Clarid).',
      'Outputs: edit results by S/V/Q code; S and V must be cleared, Q are warnings you confirm.',
      'A clean LAR feeds CFPB filing, fair lending, redlining, and the CRA lending test.',
    ],
    firstAction: 'Upload your LAR and review the edit results.',
    feeds: [
      'hmda-edit-intelligence',
      'hmda-cfpb-filing',
      'hmda-quality',
      'fair-lending',
      'fair-lending-redlining',
      'fair-lending-regression',
      'cra-lending-analysis',
      'cra-lending-test',
      'peer-benchmarking',
    ],
    needs: ['hmda-lar-builder', 'hmda-geocoding'],
    glossary: ['hmda-edit-codes', 'ffiec', 'lei', 'gmi'],
    runtime: '1–2 min',
  },
  'hmda-edit-intelligence': {
    title: 'What is Edit Intelligence?',
    summary:
      'Prioritizes and explains your HMDA edits — which to fix first and why — so you’re not working a flat list of hundreds of warnings.',
    points: [
      'Inputs: a completed validation run.',
      'Outputs: edits ranked by priority with plain-language explanations.',
      'Resolved edits flow back into a clean LAR for filing.',
    ],
    firstAction: 'Pick a validation run (not "All Validations") and work the top-priority edits.',
    feeds: ['hmda-cfpb-filing'],
    needs: ['hmda-lar-validation'],
    glossary: ['hmda-edit-codes'],
  },
  'hmda-geocoding': {
    title: 'What is HMDA Geocoding?',
    summary:
      'Resolves each loan’s property address to its census tract and county, the geography HMDA requires and that redlining and CRA analysis depend on.',
    points: [
      'Inputs: property addresses (one at a time or batch).',
      'Outputs: validated tract codes in the required format.',
      'Tracts feed LAR validation, redlining, and the CRA lending test.',
    ],
    firstAction: 'Upload a batch of addresses to geocode.',
    feeds: ['hmda-lar-validation', 'fair-lending-redlining', 'cra-lending-analysis'],
    needs: [],
    glossary: ['ffiec', 'msa-md'],
    runtime: '1–3 min',
  },
  'hmda-apor': {
    title: 'What is the APOR / Rate-Spread tool?',
    summary:
      'Looks up the Average Prime Offer Rate and computes each loan’s rate spread, which determines higher-priced (HPML) and HOEPA status for HMDA and pricing analysis.',
    points: [
      'Inputs: loan rate, terms, and lock/action dates.',
      'Outputs: the rate spread and whether the loan is higher-priced.',
      'Results inform LAR fields and fair-lending pricing.',
    ],
    firstAction: 'Select a recent validation, or enter a loan to compute its rate spread.',
    feeds: ['hmda-lar-validation', 'fair-lending'],
    needs: [],
    glossary: ['apor', 'hpml', 'hoepa'],
  },
  'hmda-cfpb-filing': {
    title: 'What is CFPB Filing?',
    summary:
      'Packages your validated LAR for submission to the CFPB HMDA Platform, including the institution LEI and the filing workflow.',
    points: [
      'Inputs: a LAR that has cleared validation.',
      'Outputs: a submission-ready file and a status workflow through filing.',
      'This is the end of the HMDA pipeline for the reporting year.',
    ],
    firstAction: 'Confirm your LEI and generate the submission file.',
    feeds: [],
    needs: ['hmda-lar-validation'],
    glossary: ['lei', 'ffiec'],
  },
  'hmda-quality': {
    title: 'What is HMDA Quality Monitoring?',
    summary:
      'Tracks the quality of your HMDA data over time and by loan officer, surfacing patterns — like recurring errors — before they become exam findings.',
    points: [
      'Inputs: your validation history.',
      'Outputs: a quality score plus breakdowns by loan officer (shown by name).',
      'Feeds board reporting and loan-officer fair-lending review.',
    ],
    firstAction: 'Open the quality score and drill into the lowest-scoring area.',
    feeds: ['board-reports', 'fair-lending-loan-officers'],
    needs: ['hmda-lar-validation'],
    glossary: [],
  },

  // ─────────────────────────────── Fair Lending ──────────────────────────────
  'fair-lending': {
    title: 'What is the Fair Lending analysis?',
    summary:
      'Runs Clarid’s Fair Lending Disparity (FLD) tests across underwriting, pricing, and steering — measuring whether prohibited-basis groups fare worse than control groups, with statistical significance.',
    points: [
      'Inputs: your validated HMDA LAR and, for consumer loans, the ALERT/ILDR file.',
      'Outputs: disparity tables by prohibited basis with odds ratios and p-values, flagged Low/Moderate/High.',
      'Findings feed risk assessments, board reports, and exam readiness.',
    ],
    firstAction: 'Run the analysis on your most recent validated LAR.',
    feeds: ['fair-lending-risk-assessments', 'board-reports', 'exam-readiness', 'compliance-hub'],
    needs: ['hmda-lar-validation', 'alert-upload', 'hmda-apor'],
    glossary: ['iflep', 'odds-ratio', 'p-value', 'mmct', 'ildr'],
    runtime: '1–3 min',
  },
  'alert-upload': {
    title: 'What is the ALERT / consumer loan upload?',
    summary:
      'Imports your non-HMDA consumer loans (the ALERT/ILDR trial-balance file) so Clarid can run fair-lending tests on auto, unsecured, and other consumer products using surname and geography proxies.',
    points: [
      'Inputs: the fixed-width ALERT/ILDR loan file.',
      'Outputs: parsed consumer loans with proxied race, ethnicity, and sex.',
      'These loans feed consumer fair-lending and regression analysis.',
    ],
    firstAction: 'Upload your ALERT/ILDR file and pick the FRB or FDIC proxy method.',
    feeds: ['fair-lending', 'fair-lending-regression'],
    needs: [],
    glossary: ['ildr', 'bisg'],
  },
  'fair-lending-risk-assessments': {
    title: 'What is a Fair Lending Risk Assessment?',
    summary:
      'A holistic, examiner-aligned view of your fair-lending risk that pulls together disparity findings, redlining, content review, and your compliance controls into one IFLEP-based report.',
    points: [
      'Inputs: your fair-lending, regression, redlining, and content findings.',
      'Outputs: a risk rating with focal points and recommended actions, generated with one click.',
      'The assessment feeds board reports and exam readiness.',
    ],
    firstAction: 'Generate an assessment once your underlying analyses have run.',
    feeds: ['board-reports', 'exam-readiness', 'compliance-hub'],
    needs: [
      'fair-lending',
      'fair-lending-regression',
      'fair-lending-redlining',
      'fair-lending-content-findings',
    ],
    glossary: ['iflep', 'mmct'],
    runtime: '2–4 min',
  },
  'fair-lending-loan-officers': {
    title: 'What is Loan Officer analysis?',
    summary:
      'Breaks fair-lending outcomes down by loan officer, so disparities that wash out at the institution level but concentrate with one originator become visible.',
    points: [
      'Inputs: your validated HMDA data with loan-officer identifiers.',
      'Outputs: per-officer metrics with a demo-completeness indicator.',
      'Notable patterns feed risk assessments.',
    ],
    firstAction: 'Sort officers by disparity and review the outliers.',
    feeds: ['fair-lending-risk-assessments'],
    needs: ['hmda-lar-validation', 'hmda-quality'],
    glossary: ['odds-ratio'],
  },
  'fair-lending-redlining': {
    title: 'What is Redlining analysis?',
    summary:
      'Tests whether your lending avoids minority neighborhoods, comparing your application and origination patterns in majority-minority tracts against a volume-matched peer group across the four redlining prongs.',
    points: [
      'Inputs: validated HMDA, geocoded tracts, your branches, and assessment areas.',
      'Outputs: MMCT vs. non-MMCT lending disparities vs. the VBAA peer set, by prong.',
      'Results feed the redlining map, risk assessments, and board reports.',
    ],
    firstAction: 'Confirm reference data is loaded, then run the redlining screen.',
    feeds: ['fair-lending-redlining-map', 'fair-lending-risk-assessments', 'board-reports'],
    needs: ['hmda-lar-validation', 'hmda-geocoding', 'branches', 'cra-assessment-areas'],
    glossary: ['mmct', 'lmi', 'rema', 'vbaa', 'ffiec'],
    runtime: '1–3 min',
  },
  'fair-lending-redlining-map': {
    title: 'What is the Redlining Map?',
    summary:
      'A geographic view of your lending, branches, and minority-tract concentration, so redlining patterns are visible on the map, not just in tables.',
    points: [
      'Inputs: a completed redlining analysis.',
      'Outputs: layered map of loans, branches, and MMCT/LMI shading.',
      'Use it alongside the redlining tables to explain findings.',
    ],
    firstAction: 'Toggle the MMCT and loan layers to see concentration.',
    feeds: [],
    needs: ['fair-lending-redlining'],
    glossary: ['mmct', 'lmi'],
  },
  'fair-lending-regression': {
    title: 'What does Regression analysis prove?',
    summary:
      'Tests whether a pricing or underwriting disparity survives once legitimate factors (credit, LTV, DTI, income) are controlled for — isolating whether a prohibited basis still explains the outcome.',
    points: [
      'Inputs: a validated dataset with outcome and control variables.',
      'Outputs: coefficients and p-values showing the residual effect of the prohibited basis.',
      'Confirmed effects strengthen the fair-lending risk assessment.',
    ],
    firstAction: 'Pick a validated dataset and choose the outcome to model.',
    feeds: ['fair-lending-risk-assessments'],
    needs: ['hmda-lar-validation', 'fair-lending'],
    glossary: ['p-value', 'odds-ratio'],
    runtime: '1–3 min',
  },
  'fair-lending-content-findings': {
    title: 'What are Content Findings?',
    summary:
      'The fair-lending view of your marketing review — content flagged for UDAAP, steering, or discouragement risk — so advertising risk feeds your fair-lending posture, not just your marketing file.',
    points: [
      'Inputs: findings from marketing, website, and social screening.',
      'Outputs: categorized fair-lending content findings with a review workflow.',
      'These feed the fair-lending risk assessment and Compliance Hub.',
    ],
    firstAction: 'Open a flagged item and complete its review note.',
    feeds: ['fair-lending-risk-assessments', 'compliance-hub'],
    needs: ['marketing-upload', 'content-library'],
    glossary: ['udaap'],
  },
  exceptions: {
    title: 'What is Exception Tracking?',
    summary:
      'Logs underwriting and pricing exceptions to policy, so you can monitor whether exceptions cluster on a prohibited basis — a key fair-lending control and exam expectation.',
    points: [
      'Inputs: exceptions you record (or import) with reason and approver.',
      'Outputs: exception rates by prohibited-basis group, with approve/deny tracking.',
      'Exception patterns inform fair-lending findings and board reporting.',
    ],
    firstAction: 'Log an exception, or import your exception log.',
    feeds: ['fair-lending', 'board-reports'],
    needs: [],
    glossary: [],
  },

  // ─────────────────────────────────── CRA ───────────────────────────────────
  cra: {
    title: 'What is the CRA Dashboard?',
    summary:
      'Your Community Reinvestment Act home base — a health score and exam-readiness checklist that summarize how your lending, services, and community development stack up across your assessment areas.',
    points: [
      'Inputs: your assessment areas, HMDA and small-business lending, and CD activity.',
      'Outputs: a CRA health score and the exam-readiness items behind it.',
      'Rolls up into board reports and exam readiness.',
    ],
    firstAction: 'Review the health score and open the lowest exam-readiness item.',
    feeds: ['board-reports', 'exam-readiness', 'compliance-hub'],
    needs: ['cra-assessment-areas'],
    glossary: ['lmi', 'mmct'],
  },
  'cra-assessment-areas': {
    title: 'What are Assessment Areas?',
    summary:
      'The geographies — counties or census tracts — where your bank is evaluated for CRA. Defining them correctly is the foundation for every CRA and redlining analysis.',
    points: [
      'Inputs: the counties/tracts your branches and lending serve.',
      'Outputs: delineated AAs with LMI and MMCT demographics and a data vintage.',
      'AAs feed the lending test, gap analysis, redlining, and CRAD.',
    ],
    firstAction: 'Add an assessment area by county, or import your AA definitions.',
    feeds: [
      'cra-lending-analysis',
      'cra-lending-test',
      'cra-gap-analysis',
      'fair-lending-redlining',
      'cra-large-bank',
      'crad-report',
    ],
    needs: [],
    glossary: ['lmi', 'mmct', 'msa-md', 'oohu'],
  },
  'cra-sb-loans': {
    title: 'What are Small Business Loans (CRA)?',
    summary:
      'Your CRA-reportable small-business and small-farm lending, used in the lending test and gap analysis and split by borrower gross annual revenue.',
    points: [
      'Inputs: your small-business loan records (CSV import).',
      'Outputs: loans classified by GAR (≤ $1M / > $1M) and geography.',
      'Feeds the lending test, gap analysis, and large-bank evaluation.',
    ],
    firstAction: 'Download the CSV template and import your small-business loans.',
    feeds: ['cra-lending-test', 'cra-gap-analysis', 'cra-large-bank'],
    needs: [],
    glossary: ['gar', 'lmi'],
  },
  'cra-lending-analysis': {
    title: 'What is Lending Analysis?',
    summary:
      'The geographic and borrower distribution of your HMDA lending across your assessment areas — the view that feeds directly into the formal CRA Lending Test.',
    points: [
      'Inputs: validated HMDA lending and your assessment areas.',
      'Outputs: distribution tables by tract income and borrower income.',
      'Feeds the Lending Test and CRAD.',
    ],
    firstAction: 'Select an assessment area to see its lending distribution.',
    feeds: ['cra-lending-test', 'crad-report', 'board-reports'],
    needs: ['hmda-lar-validation', 'cra-assessment-areas', 'hmda-geocoding'],
    glossary: ['lmi', 'oohu', 'msa-md'],
  },
  'cra-lending-test': {
    title: 'What is the Lending Test?',
    summary:
      "Three CRA Lending Test outputs, computed live from your HMDA, small business, small farm, and consumer loan data: Assessment Area Concentration (lending inside vs. outside your AAs), Geographic Distribution (by Census tract income), and Borrower's Profile (by borrower income or business revenue). The Loan-to-Deposit summary compares your bank to its peers from FDIC Call Reports.",
    points: [
      'Pick each year and product — or All CRA for HMDA, small business, small farm, and consumer together — then Generate Tables. CRA evaluations usually span multiple years — add each one.',
      'Demographic vs. market benchmark: optionally Preview the CFPB market aggregate, then Apply it to add the market column to Geographic & Borrower distribution.',
      'Loan-to-Deposit: sourced from FDIC Call Reports (your FDIC cert + last exam date). Pick the comparison peers under the LTD summary.',
    ],
    firstAction: 'Pick your HMDA year(s) and product, then Generate Tables.',
    feeds: ['crad-report', 'cra-gap-analysis', 'board-reports', 'exam-readiness'],
    needs: ['hmda-lar-validation', 'cra-assessment-areas', 'cra-sb-loans', 'peer-benchmarking'],
    glossary: ['lmi', 'oohu', 'gar', 'msa-md'],
    runtime: '1–2 min',
  },
  'cra-cd-activities': {
    title: 'What are Community Development Activities?',
    summary:
      'Your community-development loans, investments, and services — required for larger banks under the Investment and Service tests and credited in your CRA evaluation.',
    points: [
      'Inputs: CD loans, qualified investments (QI), grants, and service hours.',
      'Outputs: categorized CD activity, weighted by your bank size and tests.',
      'Feeds CRAD, the large-bank evaluation, and board reports.',
    ],
    firstAction: 'Add a CD activity, or import your CD log.',
    feeds: ['crad-report', 'cra-large-bank', 'board-reports'],
    needs: [],
    glossary: ['qi', 'lmi'],
  },
  'cra-gap-analysis': {
    title: 'What is CRA Gap Analysis?',
    summary:
      'Compares your lending against community need and market benchmarks to surface where you’re underperforming — the gaps an examiner (or your board) would ask about.',
    points: [
      'Inputs: your lending-test results and assessment-area demographics.',
      'Outputs: a weighted gap score with the specific underserved tracts/segments.',
      'Recommendations feed board reports and exam readiness.',
    ],
    firstAction: 'Open the gap score and review the highest-gap assessment area.',
    feeds: ['board-reports', 'exam-readiness'],
    needs: ['cra-lending-test', 'cra-assessment-areas'],
    glossary: ['lmi', 'mmct'],
  },
  'cra-public-file': {
    title: 'What is the CRA Public File?',
    summary:
      'The public-facing CRA file every bank must maintain — assessment areas, branch list, CRA notice, and prior evaluations — assembled and tracked here for the regulatory requirement.',
    points: [
      'Inputs: the documents and dates CRA requires you to make public.',
      'Outputs: a complete, dated public file with each required item tracked.',
      'Completeness shows up in exam readiness.',
    ],
    firstAction: 'Mark each required item on file, attaching the document and date.',
    feeds: ['exam-readiness'],
    needs: ['cra-assessment-areas', 'branches'],
    glossary: [],
  },
  'crad-report': {
    title: 'What is the CRAD report?',
    summary:
      'The CRA Analysis Document — a full, exam-style write-up of your CRA performance across the applicable tests, assembled from your lending, community development, and assessment-area data.',
    points: [
      'Inputs: your lending-test results, CD activity, and assessment areas.',
      'Outputs: a narrative-plus-tables report with per-test ratings and a roll-up.',
      'Feeds exam readiness and the board report.',
    ],
    firstAction: 'Generate the CRAD once your lending test and CD activity are in.',
    feeds: ['exam-readiness', 'board-reports'],
    needs: ['cra-lending-test', 'cra-assessment-areas', 'cra-cd-activities'],
    glossary: ['lmi', 'mmct', 'qi'],
    runtime: '2–4 min',
  },
  'cra-large-bank': {
    title: 'What is the Large Bank evaluation?',
    summary:
      'The three-test CRA methodology (Lending, Investment, Service) used for larger banks, with point scoring, five-tier ratings, and state/multistate roll-ups.',
    points: [
      'Inputs: your lending test, qualified investments, and service activity by AA.',
      'Outputs: per-test conclusions, point scores, and an institution rating.',
      'Feeds the CRAD report and exam readiness.',
    ],
    firstAction: 'Run the Large Bank evaluation across your assessment areas.',
    feeds: ['crad-report', 'exam-readiness'],
    needs: ['cra-lending-test', 'cra-cd-activities', 'cra-assessment-areas'],
    glossary: ['qi', 'lmi', 'mmct'],
    runtime: '2–5 min',
  },

  // ─────────────────────── Exam Center / Reg Intelligence ─────────────────────
  'exam-readiness': {
    title: 'What is Exam Readiness?',
    summary:
      'A single checklist of what an examiner will look for, with each open finding and recommended action linked back to the module that produced it — so you know exactly where to act.',
    points: [
      'Inputs: findings and gaps from every module (HMDA, fair lending, CRA, marketing).',
      'Outputs: a prioritized readiness checklist with source-module links.',
      'Drives what goes into your exam package.',
    ],
    firstAction: 'Open the top finding and follow its link to the source module.',
    feeds: ['exams'],
    needs: ['fair-lending', 'cra', 'hmda-quality', 'marketing-reports'],
    glossary: [],
  },
  exams: {
    title: 'What is Exam Management?',
    summary:
      'The container for an actual exam — it bundles findings, workpapers, and documents from across Clarid into one package you can hand to examiners, organized by focus area.',
    points: [
      'Inputs: exam-readiness findings, board reports, and module workpapers.',
      'Outputs: an organized, examiner-ready package per focus area.',
      'This is where the cross-module work comes together for the exam.',
    ],
    firstAction: 'Create an exam and pull in the relevant focus areas.',
    feeds: [],
    needs: ['exam-readiness', 'board-reports'],
    glossary: [],
  },
  'compliance-hub': {
    title: 'What is the Compliance Hub?',
    summary:
      'The one place that aggregates every module — Marketing, HMDA, Fair Lending, CRA, and Enforcement — into a single compliance picture. This cross-module connection is what no single-purpose tool offers.',
    points: [
      'Inputs: findings and status from every contributing module.',
      'Outputs: a unified view showing which modules feed the package and where the gaps are.',
      'Feeds your board reports and exam package.',
    ],
    firstAction: 'Review the "modules contributing" checklist to see your coverage.',
    feeds: ['board-reports', 'exams'],
    needs: ['marketing-upload', 'hmda-lar-validation', 'fair-lending', 'cra', 'enforcement-intelligence'],
    glossary: [],
  },
  'regulatory-intelligence': {
    title: 'What is Regulatory Intelligence?',
    summary:
      'Tracks regulatory changes and guidance relevant to your institution, then lets you turn an update into action — a policy, a training, or a rule — instead of leaving it as a headline.',
    points: [
      'Inputs: ingested regulatory updates and guidance.',
      'Outputs: summarized changes with a "link to module" action.',
      'Feeds policies, training, and custom rules.',
    ],
    firstAction: 'Open a recent update and link it to the affected module.',
    feeds: ['policies', 'training', 'custom-rules'],
    needs: [],
    glossary: [],
  },
  'enforcement-intelligence': {
    title: 'What is Enforcement Intelligence?',
    summary:
      'Clarid automatically tracks public enforcement actions issued by federal regulators, so you can watch what supervisors are penalizing across the industry and gauge how exposed your own institution may be to the same issues.',
    points: [
      'Where the actions come from: Clarid pulls them automatically from the public CFPB, OCC, and FDIC newsroom and press-release feeds (duplicates removed). You can also add one yourself with Ingest Action.',
      'This is industry monitoring, not a peer comparison. It shows what regulators are doing across the market — it does not rank your bank against a peer group.',
      "What “Analyze” / “Run Exposure Analysis” does: it checks each action against your own compliance footprint in Clarid — HMDA data, exception tracking, content screening, and penalty size — then scores how relevant it is (Low / Medium / High) and suggests next steps. Your data never leaves Clarid.",
    ],
    firstAction: 'Open a recent action and click Analyze to score your exposure.',
    feeds: ['exam-readiness'],
    needs: ['hmda-lar-validation', 'exceptions', 'fair-lending-content-findings'],
    glossary: ['udaap'],
  },
  'state-compliance': {
    title: 'What is State Compliance?',
    summary:
      'Surfaces the state-specific lending and consumer-protection rules that apply to your footprint, so multi-state obligations don’t fall through the federal-only cracks.',
    points: [
      'Inputs: the states your branches and lending operate in.',
      'Outputs: applicable state rules, with the related Clarid module for each.',
      'Feeds policies and custom rules.',
    ],
    firstAction: 'Confirm your operating states and review the suggested rules.',
    feeds: ['policies', 'custom-rules'],
    needs: ['branches'],
    glossary: [],
  },

  // ─────────────────────── Program / Platform / Shell ─────────────────────────
  calendar: {
    title: 'What is the Compliance Calendar?',
    summary:
      'A single calendar of compliance deadlines and tasks — filing dates, reviews, and obligations — seeded from the regulatory calendar and from due dates across other modules.',
    points: [
      'Inputs: the regulatory calendar plus dates from vendor, complaint, and product workflows.',
      'Outputs: scheduled tasks with owners and due dates.',
      'Overdue items surface on your dashboard.',
    ],
    firstAction: 'Seed the regulatory calendar, then review upcoming tasks.',
    feeds: ['dashboard'],
    needs: [],
    glossary: [],
  },
  'board-reports': {
    title: 'What are Board Reports?',
    summary:
      'Board-ready reports that pull findings from every module — fair lending, CRA, HMDA, marketing, complaints, and exceptions — into one packaged narrative for your directors.',
    points: [
      'Inputs: findings and metrics from across Clarid.',
      'Outputs: a packaged report naming each contributing module.',
      'Feeds the exam package.',
    ],
    firstAction: 'Generate a report for the period and review the contributing modules.',
    feeds: ['exams'],
    needs: ['fair-lending', 'cra', 'hmda-quality', 'marketing-reports', 'complaints', 'exceptions'],
    glossary: [],
  },
  'peer-benchmarking': {
    title: 'What is Peer Benchmarking?',
    summary:
      'Compares your lending and financial metrics against a peer group — by market and asset size — using HMDA aggregate and FDIC data, to give context that your numbers alone can’t.',
    points: [
      'Inputs: your data plus a peer group (by MSA, asset bucket, or named peers).',
      'Outputs: side-by-side denial/origination rates and financial metrics.',
      'Feeds redlining and the CRA lending test.',
    ],
    firstAction: 'Define your peer group, then run the comparison.',
    feeds: ['fair-lending-redlining', 'cra-lending-test'],
    needs: ['hmda-lar-validation'],
    glossary: ['msa-md', 'ffiec'],
  },
  policies: {
    title: 'What is Policy Management?',
    summary:
      'A library of your compliance policies with owners and review schedules, so every policy has a next-review date and nothing goes stale before an exam.',
    points: [
      'Inputs: your policy documents (uploaded or from templates).',
      'Outputs: each policy with an owner, review frequency, and status.',
      'Feeds procedures and training.',
    ],
    firstAction: 'Add a policy (or start from a template) and set its review frequency.',
    feeds: ['procedures', 'training'],
    needs: ['regulatory-intelligence'],
    glossary: [],
  },
  procedures: {
    title: 'What is Procedure Management?',
    summary:
      'The step-by-step procedures that implement your policies, with processing-time estimates and the rules each one runs — so the "how" behind each policy is documented and auditable.',
    points: [
      'Inputs: your procedures, linked to their parent policies.',
      'Outputs: documented SOPs with the rules they execute.',
      'Supports exam readiness and training.',
    ],
    firstAction: 'Create a procedure from a policy, or start from a sample SOP.',
    feeds: ['training'],
    needs: ['policies'],
    glossary: [],
  },
  complaints: {
    title: 'What is Complaint Management?',
    summary:
      'Logs and tracks consumer complaints with SLAs and classification, so patterns (including potential fair-lending or UDAAP signals) are caught and reported, not buried.',
    points: [
      'Inputs: complaints you log, classified by type and regulation.',
      'Outputs: a tracked queue with SLA status and resolution.',
      'Patterns feed board reporting; due dates feed the calendar.',
    ],
    firstAction: 'Log a complaint and classify it.',
    feeds: ['board-reports', 'calendar'],
    needs: [],
    glossary: ['udaap'],
  },
  'vendor-compliance': {
    title: 'What is Vendor Compliance?',
    summary:
      'Tracks your third-party vendors and their risk — including security attestations like SOC 2 — so vendor oversight (a standing exam topic) is documented and current.',
    points: [
      'Inputs: your vendors, with documents and a risk score.',
      'Outputs: a vendor inventory with risk methodology and required-document status.',
      'Review dates feed the calendar.',
    ],
    firstAction: 'Add a vendor and attach its risk documentation.',
    feeds: ['calendar'],
    needs: [],
    glossary: ['soc2'],
  },
  training: {
    title: 'What is Training Management?',
    summary:
      'Assigns and tracks compliance training tied to your policies and regulatory changes, so you can show examiners who was trained on what, and when.',
    points: [
      'Inputs: training programs (or templates) and your staff/groups.',
      'Outputs: completion tracking by person and topic.',
      'Due dates feed the calendar.',
    ],
    firstAction: 'Create a training program from a template and assign a group.',
    feeds: ['calendar'],
    needs: ['policies', 'regulatory-intelligence'],
    glossary: [],
  },
  regulations: {
    title: 'What is the Regulatory Knowledge Base?',
    summary:
      'A searchable reference of the regulations that govern your institution, with links to the source text and the ability to spin up a policy or training from a regulation.',
    points: [
      'Inputs: the regulation library, kept current.',
      'Outputs: plain-language references with "open on eCFR" and "create policy" actions.',
      'Feeds policies, training, and custom rules.',
    ],
    firstAction: 'Search a regulation and open it, or create a policy from it.',
    feeds: ['policies', 'training', 'custom-rules'],
    needs: [],
    glossary: [],
  },
  'product-profiles': {
    title: 'What are Product Profiles?',
    summary:
      'Defines each of your products and the compliance checklist and rules that apply to it, so marketing review and obligations run against the right requirements automatically.',
    points: [
      'Inputs: your product definitions and the checklist each requires.',
      'Outputs: per-product rule sets and recommended checklists.',
      'Feeds marketing review and the calendar.',
    ],
    firstAction: 'Create a product profile and pick its recommended checklist preset.',
    feeds: ['marketing-upload', 'calendar'],
    needs: [],
    glossary: [],
  },
  'custom-rules': {
    title: 'What are Custom Rules?',
    summary:
      'Lets you define your own screening rules — keyword, regex, or condition — on top of Clarid’s built-in checks, so institution-specific requirements get enforced in marketing and content review.',
    points: [
      'Inputs: a rule definition (start from a template), with a test panel to validate it.',
      'Outputs: an active rule that runs in marketing and website screening.',
      'Feeds marketing review and website scans.',
    ],
    firstAction: 'Start from a rule template and test it in the panel before activating.',
    feeds: ['marketing-upload', 'website-scan'],
    needs: [],
    glossary: ['udaap'],
  },
  'ai-performance': {
    title: 'What is AI Performance monitoring?',
    summary:
      'Admin view of how Clarid’s AI checks are performing — accuracy and false-positive rate — plus the integrity of the audit trail, so you can trust and tune the automated results.',
    points: [
      'Inputs: outcomes of automated checks and the audit-log chain.',
      'Outputs: accuracy / false-positive-rate metrics and audit-chain status.',
      'Use it to validate the system; this is an admin-only surface.',
    ],
    firstAction: 'Review the accuracy and false-positive-rate metrics.',
    feeds: [],
    needs: [],
    glossary: [],
  },
  dashboard: {
    title: 'What is the Dashboard?',
    summary:
      'Your launchpad — a snapshot of urgent items, recent activity, and your compliance posture, with quick paths into the modules that need attention.',
    points: [
      'Inputs: status and due dates from across Clarid.',
      'Outputs: urgent-attention counts and a setup path for new accounts.',
      'Click any urgent number to jump to the filtered list behind it.',
    ],
    firstAction: 'New here? Follow the "Getting Started" setup; otherwise, clear your urgent items.',
    feeds: [],
    needs: [],
    glossary: [],
  },
  branches: {
    title: 'What are Branches?',
    summary:
      'Your branch and office locations, geocoded to census tracts — the footprint that drives redlining branch analysis, CRA assessment areas, and your state obligations.',
    points: [
      'Inputs: your branch addresses (with type: branch, LPO, etc.).',
      'Outputs: geocoded locations with tract and MMCT context.',
      'Feeds redlining, assessment areas, and state compliance.',
    ],
    firstAction: 'Add or import your branches and confirm their geocoded tracts.',
    feeds: ['fair-lending-redlining', 'cra-assessment-areas', 'state-compliance'],
    needs: [],
    glossary: ['mmct', 'msa-md'],
  },
  organization: {
    title: 'What is the Organization view?',
    summary:
      'Manages your institution profile, teams, and members — including, for consultants, the portfolio of banks you operate — and surfaces an org-level health score.',
    points: [
      'Inputs: your institution identity, teams, and members.',
      'Outputs: an org-level health-score breakdown and member management.',
      'Sets the context every other module operates within.',
    ],
    firstAction: 'Complete your institution setup, then invite your team.',
    feeds: [],
    needs: [],
    glossary: [],
  },
};
